import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BANDWIDTH_CHECK_INTERVAL_MS,
  BANDWIDTH_SWITCH_COOLDOWN_MS,
  ICE_DISCONNECT_GRACE_MS,
  MAX_ICE_DISCONNECT_MS,
  OFFLINE_HANGUP_MS,
} from '../constant';
import { logCallError } from '../utils/callLogError';

const VIDEO_PROFILES = {
  normal: {
    width: 1280,
    height: 720,
    frameRate: 30,
    maxBitrateBps: 1_200_000, // 1.2 Mbps for 720p30
  },
  low: {
    width: 640,
    height: 360,
    frameRate: 15,
    maxBitrateBps: 350_000, // 350 kbps for 360p15
  },
};

/** Prefer RTCPeerConnection stats when available; fall back to Network Information API. */
async function shouldThrottleVideoQualityFromStats(pc) {
  if (!pc?.getStats) return null;
  try {
    const report = await pc.getStats();
    let lost = 0;
    let recv = 0;
    report.forEach((s) => {
      if (s.type === 'inbound-rtp' && s.kind === 'video') {
        lost += s.packetsLost || 0;
        recv += s.packetsReceived || 0;
      }
    });
    const total = lost + recv;
    if (total < 20) return null;
    const lossRatio = lost / total;
    return lossRatio > 0.08;
  } catch (err) {
    logCallError(err, 'call bandwidth getStats');
    return null;
  }
}

function getWeakFromNetworkInformation() {
  if (typeof navigator === 'undefined') return false;
  const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
  const effectiveType = conn?.effectiveType;
  return effectiveType === 'slow-2g' || effectiveType === '2g';
}

async function applyVideoSenderBitrate(pc, maxBitrateBps) {
  if (!pc?.getSenders || !maxBitrateBps) return;
  const sender = pc
    .getSenders()
    ?.find((s) => s?.track?.kind === 'video' && typeof s.getParameters === 'function');
  if (!sender?.setParameters) return;

  try {
    const params = sender.getParameters?.() || {};
    const encodings = Array.isArray(params.encodings) && params.encodings.length > 0 ? params.encodings : [{}];
    // Apply across all encodings so simulcast/SVC senders stay in sync.
    const nextEncodings = encodings.map((encoding) => ({ ...encoding, maxBitrate: maxBitrateBps }));
    await sender.setParameters({ ...params, encodings: nextEncodings });
  } catch (err) {
    logCallError(err, 'bandwidth sender setParameters');
  }
}

/**
 * ICE / offline handling and best-effort local video constraint adaptation.
 */
export function useCallNetworkResilience({
  activeCall,
  endCall,
  hasConnected,
  localStream,
}) {
  const [iceConnectionState, setIceConnectionState] = useState(null);
  const [isIceDisconnectedStable, setIsIceDisconnectedStable] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [bandwidthMode, setBandwidthMode] = useState('normal');

  const iceDisconnectedTimeoutRef = useRef(null);
  const iceDisconnectFailSafeTimeoutRef = useRef(null);
  const offlineHangupTimeoutIdRef = useRef(null);
  const bandwidthModeRef = useRef('normal');
  const lastBandwidthSwitchAtRef = useRef(0);

  const clearIceTimers = useCallback(() => {
    if (iceDisconnectedTimeoutRef.current) {
      clearTimeout(iceDisconnectedTimeoutRef.current);
      iceDisconnectedTimeoutRef.current = null;
    }
    if (iceDisconnectFailSafeTimeoutRef.current) {
      clearTimeout(iceDisconnectFailSafeTimeoutRef.current);
      iceDisconnectFailSafeTimeoutRef.current = null;
    }
  }, []);

  const clearOfflineTimer = useCallback(() => {
    if (offlineHangupTimeoutIdRef.current) {
      clearTimeout(offlineHangupTimeoutIdRef.current);
      offlineHangupTimeoutIdRef.current = null;
    }
  }, []);

  const clearNetworkTimers = useCallback(() => {
    clearIceTimers();
    clearOfflineTimer();
  }, [clearIceTimers, clearOfflineTimer]);

  useEffect(() => {
    const pc = activeCall?.peerConn;
    if (!pc || typeof pc.addEventListener !== 'function') {
      setIceConnectionState(null);
      return undefined;
    }

    const handler = () => {
      try {
        const ice = pc.iceConnectionState;
        const conn = pc.connectionState;
        let next = ice;
        if (ice === 'failed' || conn === 'failed') {
          next = 'failed';
        } else if (ice === 'disconnected' || conn === 'disconnected') {
          next = 'disconnected';
        } else {
          next = ice || conn || null;
        }
        setIceConnectionState(next || null);
      } catch (err) {
        logCallError(err, 'ICE / connection state handler');
      }
    };

    handler();
    pc.addEventListener('iceconnectionstatechange', handler);
    pc.addEventListener('connectionstatechange', handler);
    return () => {
      try {
        pc.removeEventListener('iceconnectionstatechange', handler);
        pc.removeEventListener('connectionstatechange', handler);
      } catch (err) {
        logCallError(err, 'ICE listener cleanup');
      }
    };
  }, [activeCall?.peerConn]);

  useEffect(() => {
    const hasEverConnected = hasConnected;
    if (iceConnectionState === 'disconnected') {
      if (iceDisconnectedTimeoutRef.current) return;
      iceDisconnectedTimeoutRef.current = setTimeout(() => {
        setIsIceDisconnectedStable(true);
        iceDisconnectedTimeoutRef.current = null;
      }, ICE_DISCONNECT_GRACE_MS);

      if (hasEverConnected && !iceDisconnectFailSafeTimeoutRef.current) {
        iceDisconnectFailSafeTimeoutRef.current = setTimeout(() => {
          setIsIceDisconnectedStable(true);
          iceDisconnectFailSafeTimeoutRef.current = null;
          try {
            if (activeCall && !activeCall.callHasEnded?.()) {
              endCall();
            }
          } catch (err) {
            logCallError(err, 'ICE fail-safe endCall');
          }
        }, MAX_ICE_DISCONNECT_MS);
      }
      return;
    }

    if (iceDisconnectedTimeoutRef.current) {
      clearTimeout(iceDisconnectedTimeoutRef.current);
      iceDisconnectedTimeoutRef.current = null;
    }
    if (iceDisconnectFailSafeTimeoutRef.current) {
      clearTimeout(iceDisconnectFailSafeTimeoutRef.current);
      iceDisconnectFailSafeTimeoutRef.current = null;
    }
    setIsIceDisconnectedStable(false);
  }, [iceConnectionState, activeCall, endCall, hasConnected]);

  useEffect(() => {
    if (!activeCall || activeCall.callHasEnded?.()) return undefined;

    setIsOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);

    const onOffline = () => {
      setIsOnline(false);
      if (offlineHangupTimeoutIdRef.current) return;
      offlineHangupTimeoutIdRef.current = setTimeout(() => {
        try {
          if (!activeCall || activeCall.callHasEnded?.()) return;
          endCall();
        } catch (err) {
          logCallError(err, 'offline hangup endCall');
        }
      }, OFFLINE_HANGUP_MS);
    };

    const onOnline = () => {
      setIsOnline(true);
      clearOfflineTimer();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      clearOfflineTimer();
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [activeCall, endCall, clearOfflineTimer]);

  useEffect(() => {
    if (!activeCall || activeCall.callHasEnded?.()) return undefined;
    if (!localStream) return undefined;

    const pc = activeCall.peerConn;

    const applyQuality = async (mode) => {
      const current = bandwidthModeRef.current;
      if (current === mode) return;

      const now = Date.now();
      if (now - lastBandwidthSwitchAtRef.current < BANDWIDTH_SWITCH_COOLDOWN_MS) return;
      lastBandwidthSwitchAtRef.current = now;

      bandwidthModeRef.current = mode;
      setBandwidthMode(mode);

      const tracks = localStream.getVideoTracks?.() || [];
      const track = tracks[0];
      if (!track?.applyConstraints) return;

      const profile = VIDEO_PROFILES[mode] || VIDEO_PROFILES.normal;
      try {
        await track.applyConstraints({
          width: { ideal: profile.width },
          height: { ideal: profile.height },
          frameRate: { ideal: profile.frameRate },
        });
        await applyVideoSenderBitrate(pc, profile.maxBitrateBps);
      } catch (err) {
        logCallError(err, 'bandwidth applyConstraints');
      }
    };

    const tick = async () => {
      let weak = getWeakFromNetworkInformation();
      if (pc?.getStats) {
        const statsWeak = await shouldThrottleVideoQualityFromStats(pc);
        if (statsWeak === true) weak = true;
      }
      if (weak) await applyQuality('low');
      else await applyQuality('normal');
    };

    void tick();
    const id = setInterval(() => {
      void tick();
    }, BANDWIDTH_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeCall, localStream]);

  const resetNetworkUiState = useCallback(() => {
    clearNetworkTimers();
    setIceConnectionState(null);
    setIsIceDisconnectedStable(false);
    setIsOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);
    bandwidthModeRef.current = 'normal';
    setBandwidthMode('normal');
  }, [clearNetworkTimers]);

  /** When Matrix replaces the live call (new callId), drop ICE UI without touching offline timers. */
  const clearIceUiState = useCallback(() => {
    setIceConnectionState(null);
    setIsIceDisconnectedStable(false);
  }, []);

  return {
    iceConnectionState,
    isIceDisconnectedStable,
    isOnline,
    bandwidthMode,
    clearNetworkTimers,
    resetNetworkUiState,
    clearIceUiState,
  };
}
